import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { VideoCard } from "../components/VideoCard";
import { VideoPlayerModal } from "../components/VideoPlayerModal";
import { Search, Play, Clock, Flame, Radio } from "lucide-react";

const CATEGORIES = ["Semua", "AI", "Robotics", "LLM", "Software"];

export default function Videos() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("search") || "");
  const cat = params.get("category") || "Semua";
  const sort = params.get("sort") || "recent";

  useEffect(() => {
    api.get("/videos/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (cat !== "Semua") qp.append("category", cat);
    const s = params.get("search");
    if (s) qp.append("search", s);
    qp.append("sort", sort);
    api.get(`/videos?${qp.toString()}`).then((r) => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [params, cat, sort]);

  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value); else p.delete(key);
    setParams(p);
  };

  const submit = (e) => {
    e.preventDefault();
    setParam("search", q);
  };

  const closeModal = () => navigate({ pathname: "/videos", search: params.toString() });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10" data-testid="videos-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="chip chip-magenta mb-3"><Radio className="w-3 h-3" /> Signal · Video</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black">Video AI &amp; Tech</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">Reel dan video pendek pilihan seputar AI, LLM, robotics, dan diskusi developer dari YouTube, TikTok, dan Instagram — disinkronkan otomatis.</p>
        </div>
        {meta && (
          <div className="font-mono text-xs text-slate-500 text-left md:text-right" data-testid="videos-meta">
            <div><span className="text-cyan1 font-bold text-2xl font-heading">{meta.total}</span> video tersimpan</div>
            {meta.last_sync && <div className="mt-1">Sinkron terakhir {new Date(meta.last_sync).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</div>}
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        <form onSubmit={submit} className="flex-1 flex items-center bg-card border border-white/10 rounded-full h-12 px-4 focus-within:border-cyan1/50">
          <Search className="w-4 h-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari judul atau channel..." className="bg-transparent flex-1 outline-none px-3 text-sm placeholder:text-slate-600" data-testid="videos-search-input" />
          <button className="text-cyan1 text-sm font-medium" data-testid="videos-search-submit">Cari</button>
        </form>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setParam("category", c === "Semua" ? "" : c)}
              className={`px-4 h-12 rounded-full border transition text-sm font-medium ${cat === c ? "border-cyan1 text-void bg-cyan1" : "border-white/10 text-slate-300 hover:border-cyan1/50 hover:text-cyan1"}`}
              data-testid={`video-filter-${c}`}>{c}</button>
          ))}
        </div>
        <div className="flex rounded-full border border-white/10 overflow-hidden h-12 self-start" data-testid="video-sort">
          <button onClick={() => setParam("sort", "")} className={`px-4 text-sm font-medium flex items-center gap-2 transition ${sort === "recent" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`} data-testid="video-sort-recent"><Clock className="w-4 h-4" /> Terbaru</button>
          <button onClick={() => setParam("sort", "popular")} className={`px-4 text-sm font-medium flex items-center gap-2 transition ${sort === "popular" ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`} data-testid="video-sort-popular"><Flame className="w-4 h-4" /> Terpopuler</button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-20">Memuat video...</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-center py-20 card" data-testid="videos-empty">
          <Play className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Belum ada video yang cocok. Sinkronisasi channel berjalan otomatis setiap beberapa jam.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="videos-grid">
          {items.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
        </div>
      )}

      {id && <VideoPlayerModal videoId={id} onClose={closeModal} />}
    </div>
  );
}
